import Vue from 'vue'

import { list, show, create, update, remove, findAll } from './vendorServices'

import { uploadToS3, getSignedUrl } from '@/services/upload/uploadService'

import { errorMessages } from '@/util/helpers'

const state = {
  vendor: {},
  vendors: [],
  vendorId: null,
  vendorLogo: null,
  vendorSearchResults: [],
  isVendorLoading: false,
}

const getters = {
  vendor: (state) => state.vendor,
  vendors: (state) => state.vendors,
  vendorId: (state) => state.vendorId,
  vendorLogo: (state) => state.vendorLogo,
  vendorLogoPath: (state) => state.vendorLogoPath,
  vendorSearchResults: (state) => state.vendorSearchResults,
  isVendorLoading: (state) => state.isVendorLoading,
}

const actions = {
  async fetchVendors({ commit, dispatch, rootState }) {
    commit('setIsLoading', true)

    const defaultParams = {
      size: rootState.paginate.itemsPerPage,
      page: rootState.paginate.currentPage - 1,
    }

    const params = { ...defaultParams, ...rootState.filters.filters }

    try {
      const { data } = await list(params)
      const { result, total } = data

      commit('setVendors', result)
      commit('setTotalPages', total)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  async fetchVendor({ commit, dispatch }, vendorId) {
    commit('setIsVendorLoading', true)

    try {
      const { data } = await show(vendorId)
      commit('setVendor', data)
      commit('setPriceTableId', data?.price_table?.id)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsVendorLoading', false)
    }
  },

  async searchVendors({ commit, dispatch }, term) {
    if (!term) return

    commit('setVendorSearchResults', [])
    commit('setIsVendorLoading', true)

    const params = { search: term }

    try {
      const { data } = await list(params)
      commit('setVendorSearchResults', data?.result)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsVendorLoading', false)
    }
  },

  async createEditVendor({ commit, dispatch }, vendor) {
    commit('setIsVendorLoading', true)

    // logo stuff
    if (vendor?.fileList) {
      // add new logo
      await dispatch('attachVendorLogo', vendor)
    } else {
      // keep old logo
      const logoData = state.vendor?.logo

      if (logoData) {
        await commit('setVendorLogo', {
          filename:  logoData.name,
          external_url: logoData.url,
          external_filename: logoData.external_name,
        })
      }
    }
    // /logo stuff

    try {
      let body = null

      if (state.vendorLogo) body = { ...vendor, logo_request: state.vendorLogo }
      else body = vendor

      body?.id ? await update(body) : await create(body)

      commit('closeAllModals')
      commit('setVendor', null)
      dispatch('fetchVendors')
      dispatch(
        'showSuccessMessage',
        Vue.i18n.translate(
          `vendors.messages.${
            body?.id ? 'successfullyUpdated' : 'successfullyCreated'
          }`
        )
      )

      commit('setVendorLogo', null)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', errorMessages(data.errors))
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsVendorLoading', false)
    }
  },

  async removeVendor({ commit, dispatch }, vendorId) {
    commit('setIsVendorLoading', true)

    try {
      await remove(vendorId)

      dispatch(
        'showSuccessMessage',
        Vue.i18n.translate('vendors.messages.successfullyDeleted')
      )
      dispatch('fetchVendors')
      commit('setVendor', null)
      commit('closeAllModals')
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsVendorLoading', false)
    }
  },

  async attachVendorLogo({ commit, dispatch }, vendor) {
    const uploadUrl = '/admin/vendor/fileUploadURL'

    try {
      for (const file of vendor?.fileList) {
        const filename = file.name
        const filetype = file.type
        const vendor_name = vendor.name

        if (filename.length < 50 && filetype) {
          const { signedUrl, external_filename } = await getSignedUrl(
            {
              filename,
              filetype,
              vendor_name,
            },
            uploadUrl
          )

          const unassignedUrl = signedUrl.substring(0, signedUrl.indexOf('?'))

          await uploadToS3(signedUrl, file)

          await commit('setVendorLogo', {
            filename,
            external_filename,
            external_url: unassignedUrl,
          })
        } else {
          commit('setIsAppointmentFilesUploading', false)
          dispatch(
            'showErrorMessage',
            Vue.i18n.translate('upload.error.failed')
          )

          return
        }
      }
    } catch (response) {
      dispatch('showErrorMessage', response.toString())
      return
    }
  },

  async findAllVendors({ commit, dispatch }, term) {
    commit('setIsLoading', true)

    try {
      const { data } = await findAll()
      commit('setVendors', data)
    } catch ({ data }) {
      data?.errors && data?.errors?.length >= 1
        ? dispatch('showErrorMessage', data.errors[0].message)
        : dispatch('showErrorMessage', data.message)
    } finally {
      commit('setIsLoading', false)
    }
  },

  handleOpenCreateEditVendorModal({ commit, dispatch }, id) {
    commit('setVendor', null)
    dispatch('openModal', 'CREATE_EDIT_VENDOR_MODAL')
    id && dispatch('fetchVendor', id)
  },

  handleDeleteVendorModal({ commit, dispatch }, id) {
    commit('setVendor', null)
    dispatch('openModal', 'DELETE_VENDOR_MODAL')
    id && dispatch('fetchVendor', id)
  },
}

const mutations = {
  setVendor: (state, vendor) => {
    state.vendor = vendor
  },

  setVendors: (state, vendors) => {
    state.vendors = vendors
  },

  setVendorId: (state, id) => {
    state.vendorId = id
  },

  setVendorLogo: (state, logo) => {
    state.vendorLogo = logo
  },

  setVendorLogoPath: (state, path) => {
    state.vendorLogoPath = path
  },

  setVendorSearchResults: (state, result) => {
    state.vendorSearchResults = result
  },

  setIsVendorLoading: (state, status) => {
    state.isVendorLoading = status
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
